import React, { useState } from 'react';
import { Button, Table, Form, Alert, Spinner } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import {
  useGetIncomesQuery,
  useBulkUpdateIncomesMutation,
  useUploadIncomeImagesMutation,
} from '../../slices/incomesApiSlice';

const BulkIncomeUpdateWithImageUploadScreen = () => {
  const [updates, setUpdates] = useState([]);
  const [results, setResults] = useState([]);
  const [fileName, setFileName] = useState('');
  const { data, isLoading: loadingIncomes } = useGetIncomesQuery({ keyword: '', pageNumber: 1 });
  const [bulkUpdateIncomes, { isLoading: loadingUpdate }] = useBulkUpdateIncomesMutation();
  const [uploadIncomeImages, { isLoading: loadingUpload }] = useUploadIncomeImagesMutation();

  const validateRow = (row) => {
    if (!row.AccountName) return 'Missing Account Name';
    if (!row.Account || !['Debit', 'Credit'].includes(row.Account)) return 'Invalid Account';
    if (isNaN(parseFloat(row.Amount))) return 'Invalid Amount';
    if (!row.Date || isNaN(Date.parse(row.Date))) return 'Invalid Date';
    return null;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file || !data?.incomes) return;

    setFileName(file.name);
    const reader = new FileReader();

    reader.onload = (evt) => {
      const workbook = XLSX.read(evt.target.result, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });

      const enriched = rows.map((row, index) => {
        const match = data.incomes.find(
          (exp) => exp.accountName.trim().toLowerCase() === row.AccountName.trim().toLowerCase()
        );
        return {
          rowIndex: index + 2,
          excelName: row.AccountName,
          matchedIncome: match,
          date: row.Date,
          account: row.Account,
          accountName: row.AccountName,
          subAccount: row.SubAccount,
          details: row.Details,
          amount: parseFloat(row.Amount),
          category: row.Category,
          notes: row.Notes,
          images: row.Images ? row.Images.split(',').map((url) => url.trim()) : [],
          error: validateRow(row),
        };
      });

      setUpdates(enriched);
      setResults([]);
      toast.success(`Loaded ${enriched.length} rows from ${file.name}`);
    };

    reader.readAsArrayBuffer(file);
  };

  const handleImageUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('images', file);

    try {
      const res = await uploadIncomeImages(formData).unwrap();
      const updated = [...updates];
      updated[index].images = res.images;
      setUpdates(updated);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err?.data?.message || err.message);
    }
  };

  const handleSubmit = async () => {
    const feedback = [];

    for (const row of updates) {
      if (row.error) {
        feedback.push({ index: row.rowIndex, status: 'Skipped', message: row.error });
        continue;
      }

      if (!row.matchedIncome) {
        feedback.push({ index: row.rowIndex, status: 'Skipped', message: 'No matching income found' });
        continue;
      }

      try {
        await bulkUpdateIncomes({
          incomeId: row.matchedIncome._id,
          date: row.date,
          account: row.account,
          accountName: row.accountName,
          subAccount: row.subAccount,
          details: row.details,
          amount: row.amount,
          category: row.category,
          notes: row.notes,
          images: row.images,
        }).unwrap();

        feedback.push({ index: row.rowIndex, status: 'Success', message: 'Updated' });
      } catch (err) {
        feedback.push({
          index: row.rowIndex,
          status: 'Failed',
          message: err?.data?.message || err.message,
        });
      }
    }

    setResults(feedback);
    toast.success('Income update process completed');
  };

  return (
    <div className="container mt-4">
      <h2>🧾 Bulk Income Update with Image Upload</h2>

      <Form.Group controlId="excelUpload" className="mb-3">
        <Form.Label>Upload Excel File (.xlsx)</Form.Label>
        <Form.Control type="file" accept=".xlsx" onChange={handleFileUpload} />
      </Form.Group>

      {updates.length > 0 && (
        <>
          <h5>Preview & Match: {fileName}</h5>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Row</th>
                <th>Excel Account Name</th>
                <th>Matched Income</th>
                <th>Date</th>
                <th>Account</th>
                <th>SubAccount</th>
                <th>Details</th>
                <th>Amount</th>
                <th>Category</th>
                <th>Category</th>
                <th>Notes</th>
                <th>Images</th>
                <th>Upload Image</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {updates.map((row, idx) => (
                <tr key={idx} style={{ background: row.error ? '#fff3cd' : 'inherit' }}>
                  <td>{row.rowIndex}</td>
                  <td>{row.excelName}</td>
                  <td>{row.matchedIncome?.accountName || '❌ Not Found'}</td>
                  <td>{row.date}</td>
                  <td>{row.account}</td>
                  <td>{row.subAccount}</td>
                  
                  <td>{row.details}</td>
                  <td>{row.amount}</td>
                  <td>{row.category}</td>
                  <td>
  <Form.Select
    value={row.category}
    onChange={(e) => {
      const updated = [...updates];
      updated[idx].category = e.target.value;
      setUpdates(updated);
    }}
  >
    <option value="">Select</option>
    {[
      'Morning', 'AfterNoon',
      'Night', 'Other'
    ].map((cat) => (
      <option key={cat} value={cat}>{cat}</option>
    ))}
  </Form.Select>
</td>
                  <td>{row.notes}</td>
                  <td>
                    {row.images.map((img, i) => (
                      <img key={i} src={img} alt="preview" style={{ width: '20px', height: '20px', marginRight: '5px' }} />
                    ))}
                  </td>
                  <td>
                    <Form.Control type="file" onChange={(e) => handleImageUpload(e, idx)} />
                    {loadingUpload && <Spinner animation="border" size="sm" />}
                  </td>
                  <td>
                    {row.error
                      ? <span className="text-danger">{row.error}</span>
                      : row.matchedIncome
                      ? <span className="text-success">✅</span>
                      : <span className="text-warning">No Match</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <Button onClick={handleSubmit} disabled={loadingUpdate}>
            {loadingUpdate ? <Spinner animation="border" size="sm" /> : 'Submit Updates'}
          </Button>
        </>
      )}

      {results.length > 0 && (
        <div className="mt-4">
          <h5>📋 Update Results</h5>
          {results.map((r, i) => (
            <Alert key={i} variant={
              r.status === 'Success' ? 'success' :
              r.status === 'Skipped' ? 'warning' :
              'danger'
            }>
              Row {r.index}: {r.status} — {r.message}
            </Alert>
          ))}
        </div>
      )}
    </div>
  );
};

export default BulkIncomeUpdateWithImageUploadScreen;